import { useCallback, useRef, useState } from "react"
import DatalakeService from "src/services/datalake-service"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"

export const MAX_COMPANY_IMPORT_SIZE = 10 * 1024 * 1024

const ALLOWED_MIME_TYPES = {
    xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
    csv: ["text/csv", "application/csv", "application/vnd.ms-excel", "text/plain"],
}

const getFileExtension = (file) => file?.name?.split(".").pop()?.toLowerCase()

export const getCompanyImportValidation = (file) => {
    const extension = getFileExtension(file)

    if (!ALLOWED_MIME_TYPES[extension]) {
        return { valid: false, message: "Selecione um arquivo .xlsx ou .csv." }
    }

    if (file.size === 0) {
        return { valid: false, message: "O arquivo não pode estar vazio." }
    }

    if (file.size > MAX_COMPANY_IMPORT_SIZE) {
        return { valid: false, message: "O arquivo deve ter no máximo 10 MiB." }
    }

    if (file.type && !ALLOWED_MIME_TYPES[extension].includes(file.type)) {
        return { valid: false, message: "O tipo do arquivo não corresponde à extensão selecionada." }
    }

    return { valid: true, extension }
}

export const useCompanyBulkImport = ({ onImported }) => {
    const datalakeServiceRef = useRef(new DatalakeService())
    const importingRef = useRef(false)
    const [selectedFile, setSelectedFile] = useState(null)
    const [isImporting, setIsImporting] = useState(false)
    const [uploadError, setUploadError] = useState("")

    const clearFile = useCallback(() => {
        if (importingRef.current) return

        setSelectedFile(null)
        setUploadError("")
    }, [])

    const selectFile = useCallback((file) => {
        if (!file) {
            clearFile()
            return false
        }

        const validation = getCompanyImportValidation(file)

        if (!validation.valid) {
            setUploadError(validation.message)
            return false
        }

        setSelectedFile(file)
        setUploadError("")

        return true
    }, [clearFile])

    const importFile = useCallback(async () => {
        if (importingRef.current || !selectedFile) return false

        const validation = getCompanyImportValidation(selectedFile)

        if (!validation.valid) {
            setUploadError(validation.message)
            return false
        }

        importingRef.current = true
        setIsImporting(true)
        setUploadError("")

        try {
            await datalakeServiceRef.current.uploadCompanyFile(selectedFile, validation.extension)
            await onImported?.()
            setSelectedFile(null)
            dispatchSnackbar("Arquivo importado com sucesso.", "success")

            return true
        } catch (error) {
            const message = "Não foi possível importar o arquivo. Tente novamente."
            setUploadError(message)
            dispatchSnackbar(message, "error")

            return false
        } finally {
            importingRef.current = false
            setIsImporting(false)
        }
    }, [onImported, selectedFile])

    return {
        selectedFile,
        isImporting,
        uploadError,
        selectFile,
        clearFile,
        importFile,
    }
}
